import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';

import Dialog from '@guestyci/foundation/Dialog';
import createStyles from '@guestyci/foundation/createStyles';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import useFeatureToggle from '@guestyci/feature-toggle-fe/useFeatureToggle';

import { selectTrialPeriod } from 'app/store/selector';
import { closPopUp as closPopUpAction } from 'app/store/trial-period.slice';
import { getActiveListings } from 'API/handlers/getCompanyInfo';

import track from 'utils/dio/trial-locked-flow';
import utils from 'utils';
import getUserData from 'utils/user';
import porterDio from 'utils/dio/login-page';
import config from 'config';
import TrialDialogContent from './TrialDialogContent';

const useClasses = createStyles((theme: typeof defaultTheme) => ({
  dialogContent: {
    width: '90vw',
    maxWidth: 1760,
    zIndex: 999999999,
    border: 'none',
    borderRadius: 20,

    '& > button:first-of-type': {
      position: 'absolute',
      top: -30,
      right: -15,
      color: theme.palette.text.white,
      '&:hover': {
        color: theme.palette.text.dark,
      },
    },

    '& > div:first-of-type': {
      borderRadius: 20,
      overflow: 'hidden',
    },
  },
}));

const REDIRECT_URL_FOR_AUTOLOGIN_FAILED = (userId: string) => {
  return config.redirectSuspendedUserUrl.replace('{userId}', userId);
};

export default function TrialPeriodSelectPopup() {
  const { dialogContent } = useClasses();
  const [isGFHTrialFlowEnabledLoading, isGFHTrialFlowEnabled] =
    useFeatureToggle('gfhLockedFlow');
  const isFtIsOff = !isGFHTrialFlowEnabledLoading && !isGFHTrialFlowEnabled;

  const {
    daysLeft,
    showSelectTrialPopUp,
    trialDuration,
    isSuspended,
    dayOfTrial,
    subscriptionCancelled,
    isFetched: isTrialInfoFetched,
  } = useSelector(selectTrialPeriod);
  const dispatch = useDispatch();
  const userData = getUserData();
  const [isShowed, setIsShowed] = useState(false);

  const handleClosePopUp = useCallback(() => {
    if (!isSuspended) {
      // if trial period is ended, do not allow user to interact with app
      dispatch(closPopUpAction());
      track.endOfTrialPopUpClickX();
      setIsShowed(false);
    }
  }, [dispatch, isSuspended]);

  const isHidden = !showSelectTrialPopUp || userData?.subscribed;

  const { isFetched, data: activeListingsCount } = useQuery({
    queryKey: 'getActiveListings',
    queryFn: getActiveListings,
    enabled: !isHidden && isGFHTrialFlowEnabled,
  });

  useEffect(() => {
    if (
      isGFHTrialFlowEnabled &&
      !isHidden &&
      !isShowed &&
      isFetched &&
      isTrialInfoFetched
    ) {
      track.endOfTrialPopUpView(
        {
          isSuspended,
          dayOfTrial,
          activeListingsCount: activeListingsCount.data,
        },
        !subscriptionCancelled
      );
      setIsShowed(true);
    }
  }, [
    isGFHTrialFlowEnabled,
    isHidden,
    dayOfTrial,
    isShowed,
    isSuspended,
    isFetched,
    activeListingsCount,
    subscriptionCancelled,
    isTrialInfoFetched,
  ]);

  useEffect(() => {
    if (userData?.accountIsSuspended || isSuspended) {
      if (isFtIsOff) {
        // Previous implementation
        porterDio.userIsSuspended(dayOfTrial);
        return window.location.replace(
          REDIRECT_URL_FOR_AUTOLOGIN_FAILED(userData?.ypaUserId)
        );
      }
    }
  }, [isFtIsOff, userData, isSuspended, dayOfTrial]);

  if (isHidden || !isGFHTrialFlowEnabled) {
    return null;
  }

  utils.setLocalStorage({ showTrialDialogOnLogin: null });

  return (
    <Dialog
      open={showSelectTrialPopUp}
      className={dialogContent}
      noClear={isSuspended}
      onClose={handleClosePopUp}
    >
      <TrialDialogContent trialDuration={trialDuration} daysLeft={daysLeft} />
    </Dialog>
  );
}
